import React from 'react';

import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { RootState } from '@share/utils';
import { getCurrencyText, renderNumber } from '@share/utils';
import { Currency } from '../currency';

import { WalletPriceType } from '@utils';

import './style.scss';

interface IProps {
  maxRewardCash?: number;

  price: number;

  /** Optional. Used in the "no rewards" message as the amount you could use (e.g. per night). If not provided and there are no rewards, nothing is displayed. */
  pricePerNight?: number;

  currency: string | null | undefined;

  type: WalletPriceType;

  style?: any;

  isShort?: boolean;
}

export function WalletRewardMessage(props: IProps): React.ReactElement | null {
  const {
    currency,
    style,
    type,
    maxRewardCash,
    price,
    pricePerNight,
    isShort,
  } = props;

  const loginStore = useSelector((state: RootState) => state.loginStore);

  const { account, userWalletReward } = loginStore;

  if (!account?.enableWalletReward) {
    return null;
  }

  const hasRewards = !!(
    maxRewardCash &&
    !isEmpty(userWalletReward) &&
    !isEmpty(userWalletReward?.allRewards)
  );

  const isSearch = type === WalletPriceType.Search;
  const isDetails = type === WalletPriceType.Details;
  const isBook = type === WalletPriceType.Book;

  const isSearchOrDetails = isSearch || isDetails;
  const isOneLine = isBook || isDetails;

  const textStyles: any = {};

  if (!isOneLine) {
    textStyles.justifyContent = 'flex-end';
  }

  const stylesMainDiv = { ...style };
  const amountFormatted = `${getCurrencyText(currency)}${renderNumber(maxRewardCash, 2)}`;

  const renderLine = (text: string, textOneline: string, amount: number, withMarginBottom: boolean = false) => {
    return (
      <div className="hotel-card-wrapper__price-public" style={{ ...stylesMainDiv, marginBottom: withMarginBottom || !isSearch ? '10px' : '0px' }}>
        {!isOneLine ? (
          <p className={`hotel-card-wrapper__commission-text`} style={{ textAlign: 'end', width: '100%' }}>
            <FormattedMessage
              id={text}
              values={{ amountFormatted: `${getCurrencyText(currency)}${renderNumber(amount, 2)}` }}
            />
          </p>
        ) : (
          <>
            <p className={`hotel-card-wrapper__commission-text`}>
              <FormattedMessage id={textOneline} />
            </p>
            <p className={`hotel-card-wrapper__commission-text`}>
              <Currency currency={currency} />{renderNumber(amount, 2)}
            </p>
          </>
        )}
      </div>
    );
  };

  // At checkout, reward is shown in the single price breakdown (reward applied line). No duplicate section.
  if (isBook) {
    return null;
  }

  if (!hasRewards && (pricePerNight === undefined || pricePerNight === null)) {
    return null;
  }

  const amountNoRewards = `${getCurrencyText(currency)}${renderNumber(pricePerNight ?? 0, 2)}`;
  const noRewardsMessage = (
    <p className="wallet-reward-message__card-total-after">
      <FormattedMessage id="wallet.reward.no_rewards_use_up_to" values={{ amount: amountNoRewards }} />
    </p>
  );

  if (isSearchOrDetails) {
    return (
      <div
        className="wallet-message-container wallet-reward-message--search-details"
        style={{
          ...textStyles,
          marginTop: '8px',
          marginLeft: !isSearch ? '15px' : '0px',
          marginRight: !isSearch ? '15px' : '0px',
          marginBottom: '15px',
        }}
      >
        {!hasRewards ? (
          <div className="wallet-reward-message__card">
            <p className="wallet-reward-message__card-title">
              🎁 <FormattedMessage id="wallet.reward.wallet_title" />
            </p>
            {noRewardsMessage}
          </div>
        ) : isShort ? (
          <div className="hotel-card-wrapper__price-public" style={stylesMainDiv}>
            <p className={`hotel-card-wrapper__commission-text`} style={{ fontSize: '12px' }}>
              <FormattedMessage id="wallet.reward.saving_reward" values={{ amountFormatted }} />
            </p>
          </div>
        ) : (
          <div className="wallet-reward-message__card">
            <p className="wallet-reward-message__card-title">
              🎁 <FormattedMessage id="wallet.reward.wallet_title" />
            </p>
            <p className="wallet-reward-message__card-amount">
              <FormattedMessage id="wallet.reward.you_have_available" values={{ amount: amountFormatted }} />
            </p>
            <p className="wallet-reward-message__card-total-after">
              <FormattedMessage
                id="wallet.reward.total_after_rewards"
                values={{ amount: `${getCurrencyText(currency)}${renderNumber(Math.max(0, price - (maxRewardCash ?? 0)), 2)}` }}
              />
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`wallet-message-container${isBook ? ' wallet-reward-message--book' : ''}${!hasRewards ? ' wallet-reward-message--search-details' : ''}`}
      style={{
        ...textStyles,
        marginTop: '8px',
        marginLeft: !isSearch ? '15px' : '0px',
        marginRight: !isSearch ? '15px' : '0px',
        marginBottom: '15px',
      }}
    >
      {hasRewards ? (
        <>
          {isShort ? (
            <div className="hotel-card-wrapper__price-public" style={stylesMainDiv}>
              <p
                className={`hotel-card-wrapper__commission-text`}
                style={{ fontSize: '12px' }}
              >
                <FormattedMessage
                  id="wallet.reward.saving_reward"
                  values={{ amountFormatted: `${getCurrencyText(currency)}${renderNumber(maxRewardCash, 2)}` }}
                />
              </p>
            </div>
          ) : (
            <>
              <div className="hotel-card-wrapper__price-public" style={{ ...stylesMainDiv, marginBottom: '10px' }}>
                <p className={`hotel-card-wrapper__commission-text`} style={{ textAlign: 'start', width: '100%' }}>
                  <FormattedMessage id="rewards" />:
                </p>
              </div>

              {isBook ? (
                <div className="hotel-card-wrapper__price-public" style={{ marginTop: '10px', marginBottom: '7px' }}>
                  <p className="hotel-card-wrapper__public-text rate">
                    <FormattedMessage id="wallet.client.cash.rate" />
                  </p>
                  <p className="hotel-card-wrapper__public-text rate">
                    <Currency currency={currency} />{renderNumber((Math.floor((price ?? 0) * 100) / 100), 2)}
                  </p>
                </div>
              ) : null}

              {renderLine('wallet.reward.save_with_amount', 'wallet.reward.save_with_rewards', maxRewardCash, true)}

              {renderLine('wallet.reward.price_after_reward_amount', 'wallet.reward.price_after_reward', price - maxRewardCash)}
            </>
          )}
        </>
      ) : (
        <div className="wallet-reward-message__card">
          <p className="wallet-reward-message__card-title">
            🎁 <FormattedMessage id="wallet.reward.wallet_title" />
          </p>
          {noRewardsMessage}
        </div>
      )}
    </div>
  );
}
